import type { Market, SupplierSettings } from "@getaways/tours";
import { api, Badge, Banner, Checkbox, Empty, Field, optionsOf, PageHead, Panel, Select, useAsync, useToast, type Notice } from "@getaways/extranet-ui";
import { useState } from "react";
import { useCan } from "../lib/access";

const SUPPLIERS: Record<string, string> = { gadventures: "G Adventures" };

/** Commercial settings per supplier (open for booking, price) and the markets that set the selling currency. */
export function SuppliersPage() {
  const can = useCan();
  const suppliers = useAsync(() => api<{ suppliers: SupplierSettings[] }>("GET", "/v1/admin/suppliers"), []);
  const markets = useAsync(() => api<{ markets: Market[] }>("GET", "/v1/admin/markets"), []);
  const [edit, setEdit] = useState<SupplierSettings>();
  const [notice, setNotice] = useState<Notice>();
  const toast = useToast();

  const save = async () => {
    if (!edit) return;
    try {
      const { supplier, ...patch } = edit;
      await api("PUT", `/v1/admin/suppliers/${supplier}`, patch);
      setNotice(undefined);
      toast(`${SUPPLIERS[supplier] ?? supplier} saved.`);
      setEdit(undefined);
      suppliers.reload();
    } catch (err) {
      setNotice({ tone: "bad", text: err instanceof Error ? err.message : String(err) });
    }
  };

  return (
    <>
      <PageHead title="Suppliers and markets" sub="A supplier stays closed for booking until you check its settings and open it." />
      {(suppliers.error || notice) && <Banner notice={notice ?? { tone: "bad", text: suppliers.error! }} onClose={() => setNotice(undefined)} />}
      <div className="card" style={{ padding: 0 }}>
        <h2 style={{ padding: "12px 14px 0" }}>Suppliers</h2>
        <table className="table">
          <thead><tr><th>Supplier</th><th>Booking</th><th>Price</th><th /></tr></thead>
          <tbody>
            {(suppliers.data?.suppliers ?? []).map((s) => (
              <tr key={s.supplier}>
                <td>{SUPPLIERS[s.supplier] ?? s.supplier}<div className="small muted mono">{s.supplier}</div></td>
                <td>{s.bookable ? <Badge tone="ok">open</Badge> : <Badge>closed</Badge>}</td>
                <td className="small">{s.pricingModel === "public_price" ? "The operator's public price" : `Net + ${s.markupPct}%`}</td>
                <td>{can("settings:manage") && <button onClick={() => setEdit(structuredClone(s))}>Edit</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="card" style={{ padding: 0 }}>
        <h2 style={{ padding: "12px 14px 0" }}>Markets</h2>
        {markets.data && markets.data.markets.length === 0 ? <Empty>No markets.</Empty> : (
          <table className="table">
            <thead><tr><th>Market</th><th>Currency</th><th>Brands</th><th>Status</th></tr></thead>
            <tbody>
              {(markets.data?.markets ?? []).map((m) => (
                <tr key={m.code}><td>{m.name}<div className="small muted mono">{m.code}</div></td><td>{m.currency}</td><td className="small">{m.brands.join(", ") || "—"}</td><td>{m.active ? <Badge tone="ok">active</Badge> : <Badge>inactive</Badge>}</td></tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {edit && (
        <Panel title={SUPPLIERS[edit.supplier] ?? edit.supplier} onClose={() => setEdit(undefined)} actions={<><button className="primary" onClick={() => void save()}>Save</button><button onClick={() => setEdit(undefined)}>Cancel</button></>}>
          <Checkbox checked={edit.bookable} onCheckedChange={(bookable) => setEdit({ ...edit, bookable })} label="Open for booking" />
          <Field label="Price"><Select value={edit.pricingModel} onChange={(pricingModel) => setEdit({ ...edit, pricingModel })} options={optionsOf<SupplierSettings["pricingModel"]>({ public_price: "The operator's public price", net_markup: "What we pay + markup" })} /></Field>
          <Field label="Markup %" help="With what we pay + markup"><input type="number" min={0} max={100} value={edit.markupPct} onChange={(e) => setEdit({ ...edit, markupPct: Number(e.target.value) })} /></Field>
        </Panel>
      )}
    </>
  );
}
